import { useCallback, useEffect, useRef, useState } from "react"

import type {
  InvestigationFinding,
  InvestigationFindingsSnapshot,
} from "../utils/investigationFindings"

// A fix-request preview pins the finding revision it expects, so a retry needs
// the refreshed finding handed back, not just a re-render later. This hook owns
// the findings hook's refresh scheduling: the token that restarts the load,
// fire-and-forget refetch, and a promised refreshFinding whose resolver the
// load settles — "found" with the fresh snapshot's copy of the finding,
// "absent" when a complete successful load proves the finding is no longer
// listed, or "unverified" when the load failed or stopped truncated, so the
// caller knows it cannot claim the finding's current state.
export type InvestigationFindingRefresh =
  | { finding: InvestigationFinding; kind: "found" }
  | { finding: null; kind: "absent" | "unverified" }

export function useFindingsRefreshQueue(): {
  refetch: () => void
  refreshFinding: (findingId: string) => Promise<InvestigationFindingRefresh>
  refreshToken: number
  settleFindingRefreshes: (
    snapshot: InvestigationFindingsSnapshot | null,
    loadGeneration: number,
  ) => void
} {
  const [refreshToken, setRefreshToken] = useState(0)
  const generation = useRef(0)
  const pending = useRef<
    Array<{
      findingId: string
      generation: number
      resolve: (result: InvestigationFindingRefresh) => void
    }>
  >([])
  const refetch = useCallback(() => {
    setRefreshToken(++generation.current)
  }, [])
  const refreshFinding = useCallback(
    (findingId: string) =>
      new Promise<InvestigationFindingRefresh>((resolve) => {
        const next = ++generation.current
        pending.current.push({ findingId, generation: next, resolve })
        setRefreshToken(next)
      }),
    [],
  )
  const settleFindingRefreshes = useCallback(
    (
      snapshot: InvestigationFindingsSnapshot | null,
      loadGeneration: number,
    ) => {
      const refreshes = pending.current.filter(
        (value) => value.generation <= loadGeneration,
      )
      pending.current = pending.current.filter(
        (value) => value.generation > loadGeneration,
      )
      for (const refresh of refreshes) {
        refresh.resolve(settleRefresh(snapshot, refresh.findingId))
      }
    },
    [],
  )
  // Unmounting strands queued refreshes: no load is left to settle them, so
  // the awaiting request flow would hang on its promise forever. Resolve what
  // is still queued as unverified when the consumer goes away.
  useEffect(() => {
    return () => {
      const refreshes = pending.current
      pending.current = []
      for (const refresh of refreshes) {
        refresh.resolve({ finding: null, kind: "unverified" })
      }
    }
  }, [])
  return {
    refetch,
    refreshFinding,
    refreshToken,
    settleFindingRefreshes,
  }
}

// A truncated snapshot still has unvisited pages, so only a complete load can
// confirm a finding is gone; otherwise its absence is merely unverified.
function settleRefresh(
  snapshot: InvestigationFindingsSnapshot | null,
  findingId: string,
): InvestigationFindingRefresh {
  const finding =
    snapshot?.findings.find((value) => value.findingId === findingId) ?? null
  if (finding) {
    return { finding, kind: "found" }
  }
  return {
    finding: null,
    kind: snapshot && !snapshot.truncated ? "absent" : "unverified",
  }
}
